import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import {DateRange} from "react-day-picker";

dayjs.extend(utc);
dayjs.extend(timezone);

const localTz = dayjs.tz.guess();

export const dateTimeFormatPattern = "YYYY-MM-DD[T]HH:mm:ssZZ";
export const dateFormatPattern = "DD-MM-YY";

export const formatToShortDate = (date?: string | Date): string => {
    if (!date) {
        return "..-..";
    }

    dayjs.tz.guess()

    return dayjs(date).tz(localTz).format("DD-MM");
};

export const formatDate = (date?: string | Date): string => {
    if (!date) {
        return "..-..-....";
    }

    return dayjs(date).tz(localTz).format("DD-MM-YYYY");
};

export const formatLocalDate = (date: Date): string => {
    return dayjs(date).tz(localTz).format("YYYY-MM-DD");
};

export const formatDateTime = (date?: string | Date): string => {
    if (!date) {
        return "..-..-.... ..:..";
    }

    return dayjs(date).tz(localTz).format("DD-MM-YYYY HH:mm");
};

export const formatDateTimeSeconds = (date?: string | Date): string => {
    if (!date) {
        return "..-..-.... ..:..:..";
    }

    return dayjs(date).tz(localTz).format("DD-MM-YYYY HH:mm:ss");
};

export type DayItem = {
    formattedDay: string; // bijv. 8-mei
    date: Date;
};

export const getDaysBetween = ({from, to}: DateRange): DayItem[] => {
    if (!from || !to) {
        throw Error("Cannot determine range of undefined")
    }

    const startDay = dayjs(from);
    const endDay = dayjs(to);

    const result: DayItem[] = [];

    let current = startDay;

    while (current.isBefore(endDay, "day") || current.isSame(endDay, "day")) {
        result.push({
            formattedDay: current.format(dateFormatPattern).toLowerCase(), // 8-mei
            date: current.toDate(),
        });

        current = current.add(1, "day");
    }

    return result;
};

export type HhMm = {
    hours: number,
    minutes: number,
};

export const minutesToHhMm = (minutes: number): HhMm => ({
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
});

export const localDateFormatPattern = "YYYY-MM-DD";

/**
 * Formats a date as local `YYYY-MM-DD`, as expected by the API (e.g. for a
 * `LocalDate` parameter). Unlike `toISOString()` this doesn't shift to UTC,
 * which would give yesterday between 00:00 and 02:00.
 */
export const toLocalDateString = (date: Date): string =>
    dayjs(date).format(localDateFormatPattern);

/**
 * Parses `YYYY-MM-DD` as a local date (midnight in the local time zone).
 */
export const fromLocalDateString = (value: string): Date =>
    dayjs(value, localDateFormatPattern).toDate();

/**
 * Whether the given date falls on today (local time zone).
 */
export const isToday = (date: Date): boolean =>
    dayjs(date).isSame(dayjs(), "day");
